// @ts-strict-ignore
import Space from 'antd/lib/space';
import * as React from 'react';
import {
    AppDistributionService,
    AppPlatform,
    BetaBuild,
    BetaTester,
    BetaTestingGroup,
    BuildStatus,
    IntegrationCapability,
    IntegrationInstallationId,
    ReleaseCandidateBuildInfo,
    ReleaseStepStatus,
    ReleaseStepType,
    RollbackBuildInfo,
    RollbackResigningStrategy,
    StabilityMonitoringService,
    WorkflowAndBranchData,
} from '../../types';
import { antGrey, releaseStepDetailsContainerModuleStyle, runwayColors, tagStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import { getIsLoading, isAndroidPlatform } from '../../helpers';
import { displayTextForBuildStatus, statusColorForBuildStatus } from '../../helpers/displayHelpers';
import { dayjs } from '@/extendedDayjs';
import { getLocaleDateTimeFormat } from '../../timeHelpers';
import Divider from 'antd/lib/divider';
import Tag from 'antd/lib/tag';
import Collapse from 'antd/lib/collapse';
import ArtifactsModule from './ArtifactsModule';
import { AppBuildComponent } from '../BuildComponents';
import { Nullable, ReleaseStepComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import BetaWhatToTestModule from '../betaModules/BetaWhatToTestModule';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { KeyOutlined, ThunderboltFilled } from '@ant-design/icons';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { ReleaseUpdatable } from '../../actions/updatable';
import { tryUpdateRelease } from '../../actions/release';
import BetaGroupsModule from '../betaModules/BetaGroupsModule';
import WorkflowBuildModule from '@/components/workflowModule/WorkflowBuildModule';
import { selectAppTimeFormatSetting } from '../../selectors/selectors';

interface RollbackBuildModuleProps extends ReleaseStepComponentProps {
    releaseStepStatus: ReleaseStepStatus;
    platform: AppPlatform;
    appIdentifiers: Map<IntegrationCapability, string>;
    version: string;
    enableRetry: boolean;
    betaGroups: BetaTestingGroup[];
    betaTesters: BetaTester[];
    buildInfo?: RollbackBuildInfo;
    moduleTitle?: string;
    services: Map<IntegrationInstallationId, AppDistributionService>;
    stabilityMonitoring?: StabilityMonitoringService;
    betaBuild?: BetaBuild;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    autoApplyBetaBuildNotesAutomationIsActive: boolean;
    autoAssignBetaGroupsEnabled: boolean;
    strategy: RollbackResigningStrategy;

    // for CI signed rollbacks
    ciBuild: Nullable<ReleaseCandidateBuildInfo>;
    workflowData: Nullable<WorkflowAndBranchData>;
    autoUploadBinaryArtifactToAppStoreAutomationId: string;
    autoUploadDsymsToStabilityMonitoringAutomationId: string;
    autoDownloadArtifactsAutomationId: string;
}

const RollbackBuildModule: React.FC<RollbackBuildModuleProps> = (props: RollbackBuildModuleProps) => {
    const buildInfo = props.buildInfo;
    const associatedAppStoreBuild = buildInfo?.associatedAppStoreBuild;

    const tagColor = statusColorForBuildStatus(buildInfo?.status);
    const buildStatusText = displayTextForBuildStatus(buildInfo?.status);
    const finishedAtMoment = buildInfo?.finishedAt ? dayjs(buildInfo.finishedAt) : null;
    const artifacts = buildInfo ? buildInfo.artifacts : null;

    const defaultActiveKey =
        buildInfo?.status == BuildStatus.InProgress || buildInfo?.status == BuildStatus.Success ? ['build'] : [];

    const dispatch = useDispatch();
    const isLoading = useSelector((state: RootState) =>
        getIsLoading(ReleaseUpdatable.TryStartResignSequence, state, props.releaseId),
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));

    const onClickResign = () => {
        const request = {
            orgId: props.orgId,
            appId: props.appId,
            releaseId: props.releaseId,
        };
        dispatch(tryUpdateRelease(ReleaseUpdatable.TryStartResignSequence, request, request.releaseId));
    };

    const retryButton = (
        <PermissionedActionButton
            actionType={RunwayUserAction.UAStartRollbackResigningSequence}
            releaseId={props.releaseId}
            onClick={(event) => {
                event.stopPropagation();
                onClickResign();
            }}
            icon={<KeyOutlined />}
            loading={props.enableRetry && isLoading}
            disabled={!props.enableRetry}
        >
            Retry re-signing
        </PermissionedActionButton>
    );

    return (
        <div
            style={{
                marginBottom: 24,
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                minWidth: 0,
                gap: 18,
            }}
        >
            {props.moduleTitle && (
                <Space>
                    <ThunderboltFilled style={{ color: runwayColors.primary }} />
                    <Text style={{ fontSize: 20, color: antGrey.grey7 }}>{props.moduleTitle}</Text>
                </Space>
            )}
            {props.strategy === RollbackResigningStrategy.CIResign && (
                <WorkflowBuildModule
                    moduleTitle={'CI rebuild'}
                    buildInfo={props.ciBuild}
                    workflowData={props.workflowData}
                    releaseStepStatus={props.releaseStepStatus}
                    platform={props.platform}
                    autoUploadDsymsToStabilityMonitoringAutomationId={
                        props.autoUploadDsymsToStabilityMonitoringAutomationId
                    }
                    autoUploadBinaryArtifactToAppStoreAutomationId={
                        props.autoUploadBinaryArtifactToAppStoreAutomationId
                    }
                    autoDownloadArtifactsAutomationId={props.autoDownloadArtifactsAutomationId}
                    releaseStepType={ReleaseStepType.RollbackBuild}
                    releaseId={props.releaseId}
                    appId={props.appId}
                    orgId={props.orgId}
                    stepRunComponentProps={props.stepRunComponentProps}
                />
            )}
            {props.strategy === RollbackResigningStrategy.LocalResign && (
                <div
                    style={{
                        ...releaseStepDetailsContainerModuleStyle,
                    }}
                >
                    <Collapse defaultActiveKey={defaultActiveKey} ghost>
                        <Collapse.Panel
                            className="no-padding-collapse-panel"
                            key={'build'}
                            extra={retryButton}
                            header={
                                <Space align="center">
                                    <Tag color={tagColor} style={{ ...tagStyle }}>
                                        {buildStatusText}
                                    </Tag>
                                    <Space split={<Divider type="vertical"></Divider>} align="center">
                                        {finishedAtMoment && (
                                            <Text type="secondary">
                                                Completed at{' '}
                                                {finishedAtMoment.format(
                                                    getLocaleDateTimeFormat('shortWithComma', use24HourTimeFormat),
                                                )}
                                            </Text>
                                        )}
                                        {associatedAppStoreBuild && (
                                            <AppBuildComponent
                                                integrationId={associatedAppStoreBuild.integrationId}
                                                buildIdentifier={associatedAppStoreBuild.buildNumber}
                                                isProcessed={associatedAppStoreBuild.isProcessed}
                                            />
                                        )}
                                    </Space>
                                </Space>
                            }
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {!!buildInfo && (
                                    <ArtifactsModule
                                        appId={props.appId}
                                        orgId={props.orgId}
                                        releaseId={props.releaseId}
                                        version={props.version}
                                        artifacts={artifacts ?? []}
                                        services={props.services}
                                        stabilityMonitoring={props.stabilityMonitoring}
                                        appIdentifiers={props.appIdentifiers}
                                        buildStatus={buildInfo.status}
                                        canRetry={false}
                                        showHeader={true}
                                        isPublic={false}
                                        stepRunComponentProps={props.stepRunComponentProps}
                                    />
                                )}
                                {associatedAppStoreBuild && !isAndroidPlatform(props.platform) && (
                                    <BetaWhatToTestModule
                                        autoApplyBetaBuildNotesAutomationIsActive={
                                            props.autoApplyBetaBuildNotesAutomationIsActive
                                        }
                                        stepRunComponentProps={props.stepRunComponentProps}
                                        orgId={props.orgId}
                                        appId={props.appId}
                                        releaseId={props.releaseId}
                                        externalBuildId={associatedAppStoreBuild.id}
                                        betaNotes={buildInfo?.testerNotes ? [buildInfo.testerNotes] : []}
                                        betaIntegrationType={associatedAppStoreBuild.integrationId}
                                        editTestInformationDisabled={false}
                                    />
                                )}
                                {props.betaBuild && (
                                    <BetaGroupsModule
                                        autoAssignBetaGroupsEnabled={props.autoAssignBetaGroupsEnabled}
                                        stepRunComponentProps={props.stepRunComponentProps}
                                        allTesters={props.betaTesters}
                                        allGroups={props.betaGroups}
                                        selectedTesters={props.betaBuild.betaTesters}
                                        selectedGroups={props.betaBuild.betaGroups}
                                        externalBuildId={props.betaBuild.appStoreBuild.appStoreBuild.id}
                                        betaIntegrationType={props.betaBuild.appStoreBuild.appStoreBuild.integrationId}
                                        editGroupsDisabled={false}
                                        releaseId={props.releaseId}
                                        appId={props.appId}
                                        orgId={props.orgId}
                                    />
                                )}
                            </div>
                        </Collapse.Panel>
                    </Collapse>
                </div>
            )}
        </div>
    );
};

export default RollbackBuildModule;
